import type {
  DocumentMetadataUserEditLogEntry,
  DocumentMetadataUserEditValues,
  DocumentPageMetadataUserEditValues,
  MetadataFieldAudit,
} from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import {
  DOCUMENT_METADATA_USER_EDIT_VALUE_KEYS,
  DOCUMENT_PAGE_METADATA_USER_EDIT_VALUE_KEYS,
} from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import type { SystemDocumentJSON } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import type { SystemDocumentPageJSON } from 'features/knowledgeDocumentViewer/types/systemDocumentPageSchemas'

function asNullableString(value: unknown): string | null {
  if (typeof value !== 'string') return null
  return value
}

function fieldAudit(
  generatedValue: string | null,
  userValue: string | null
): MetadataFieldAudit {
  return {
    generated_value: generatedValue,
    user_value: userValue,
  }
}

/**
 * Normalize a values object for persistence.
 * Only includes keys present on the input — missing keys are omitted so
 * callers cannot accidentally null-clear sibling fields via Partial patches.
 */
export function buildDocumentMetadataUserEditValues(
  values: DocumentMetadataUserEditValues
): DocumentMetadataUserEditValues {
  const next: DocumentMetadataUserEditValues = {}
  for (const key of DOCUMENT_METADATA_USER_EDIT_VALUE_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(values, key)) continue
    next[key] = values[key] ?? null
  }
  return next
}

export function buildDocumentPageMetadataUserEditValues(
  values: DocumentPageMetadataUserEditValues
): DocumentPageMetadataUserEditValues {
  const next: DocumentPageMetadataUserEditValues = {}
  for (const key of DOCUMENT_PAGE_METADATA_USER_EDIT_VALUE_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(values, key)) continue
    next[key] = values[key] ?? null
  }
  return next
}

/** Full allowlisted snapshot, including explicit nulls. */
export function buildFullDocumentPageMetadataUserEditValues(
  values: DocumentPageMetadataUserEditValues
): DocumentPageMetadataUserEditValues {
  const next: DocumentPageMetadataUserEditValues = {}
  for (const key of DOCUMENT_PAGE_METADATA_USER_EDIT_VALUE_KEYS) {
    next[key] = values[key] ?? null
  }
  return next
}

export function buildGeneratedPageMetadataValues(
  systemData: Partial<SystemDocumentPageJSON> | undefined
): DocumentPageMetadataUserEditValues {
  const next: DocumentPageMetadataUserEditValues = {}
  for (const key of DOCUMENT_PAGE_METADATA_USER_EDIT_VALUE_KEYS) {
    const value = systemData?.[key]
    next[key] = typeof value === 'string' ? value : null
  }
  return next
}

export function isBlankMetadataValue(
  value: string | null | undefined
): boolean {
  if (value === null || value === undefined) return true
  return value.trim().length === 0
}

export function arePageMetadataValuesEmpty(
  values: DocumentPageMetadataUserEditValues
): boolean {
  return DOCUMENT_PAGE_METADATA_USER_EDIT_VALUE_KEYS.every((key) =>
    isBlankMetadataValue(values[key])
  )
}

export function buildDocumentMetadataEditLogEntry(args: {
  editedAt: string
  editedBy: string
  systemData: Partial<SystemDocumentJSON> | undefined
  userValues: DocumentMetadataUserEditValues
}): DocumentMetadataUserEditLogEntry {
  const { editedAt, editedBy, systemData, userValues } = args
  const entry: DocumentMetadataUserEditLogEntry = {
    edited_at: editedAt,
    edited_by: editedBy,
  }
  for (const key of DOCUMENT_METADATA_USER_EDIT_VALUE_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(userValues, key)) continue
    entry[key] = fieldAudit(
      asNullableString(systemData?.[key]),
      userValues[key] ?? null
    )
  }
  return entry
}

export function appendEditLog<T>(
  priorLog: readonly T[] | null | undefined,
  nextRow: T
): T[] {
  return [...(priorLog ?? []), nextRow]
}
